const ROLE_LABELS = {
  ADMIN: 'Admin',
  WALIKELAS: 'Walikelas',
  GURU: 'Guru',
  SISWA: 'Siswa',
  ORTU: 'Orang Tua',
};

/**
 * A *pure* Admin Keuangan: holds is_payment_admin but no admin/walikelas tier. Such an
 * account has no dashboard/sidebar access and must land on /tagihan. Used by the route
 * guard, the sidebar matrix, and the post-login redirect so the rule stays consistent.
 * @param {Object} role - role object (e.g. state.Role.value)
 * @returns {boolean}
 */
export const isPaymentAdminOnly = (role = {}) => {
  const schoolRoles = Array.isArray(role?.school_role) ? role.school_role : [];
  return (
    Boolean(role?.is_payment_admin) &&
    !role?.is_admin &&
    !schoolRoles.includes('WALIKELAS') &&
    role?.class_role !== 'WALIKELAS'
  );
};

export const formatSchoolRoleLabels = (roles = [], options = {}) => {
  const { isPaymentAdmin = false, isAdmin = false, adminSupersedesWalikelas = false } = options;
  const normalizedRoles = (Array.isArray(roles) ? roles : []).map((role) => String(role || '').trim()).filter(Boolean);

  const labels = [];

  const hasAdmin = isAdmin || normalizedRoles.includes('ADMIN');

  if (hasAdmin) {
    labels.push(ROLE_LABELS.ADMIN);
  }

  if (isPaymentAdmin) {
    labels.push('Admin Keuangan');
  }

  // ** Admin tier supersedes Walikelas in role-switch list (AC #6: highest tier listened).
  if (normalizedRoles.includes('WALIKELAS') && !(adminSupersedesWalikelas && hasAdmin)) {
    labels.push(ROLE_LABELS.WALIKELAS);
  }

  if (
    normalizedRoles.includes('GURU') &&
    !normalizedRoles.includes('ADMIN') &&
    !isAdmin &&
    !isPaymentAdmin &&
    !normalizedRoles.includes('WALIKELAS')
  ) {
    labels.push(ROLE_LABELS.GURU);
  }

  if (normalizedRoles.includes('ORTU')) {
    labels.push(ROLE_LABELS.ORTU);
  }

  if (normalizedRoles.includes('SISWA')) {
    labels.push(ROLE_LABELS.SISWA);
  }

  return labels.length > 0 ? labels.join(' / ') : ROLE_LABELS.GURU;
};

export const formatCurrentRoleLabel = (role = {}, t = (key) => ROLE_LABELS[key?.replace('role.', '')] || key) => {
  const schoolRoles = Array.isArray(role?.school_role) ? role.school_role : [];
  const hasCurrentSchool = Boolean(role?.school_id);
  const hasCurrentRole = Boolean(role?.class_role) || role?.is_admin === true || role?.is_payment_admin === true;

  if (!hasCurrentSchool || !hasCurrentRole) {
    return '-';
  }

  if (role?.is_payment_admin === true) {
    return formatSchoolRoleLabels([role?.class_role, ...schoolRoles], {
      isPaymentAdmin: true,
      isAdmin: Boolean(role?.is_admin),
      adminSupersedesWalikelas: true,
    });
  }

  if (role?.is_admin === false) {
    return t(`role.${role?.class_role}`);
  }

  return t('role.ADMIN');
};

export const formatTopbarRoleLabel = (role = {}, t) => {
  const roleLabel = formatCurrentRoleLabel(role, t);

  if (roleLabel === '-' || role?.is_payment_admin === true) {
    return roleLabel;
  }

  return `${roleLabel} - ${role?.class_name}`;
};

export const formatUserRoleLabels = (user = {}) => {
  const classRoles = (user?.class_reference ?? []).map((item) => item?.role).filter(Boolean);
  const schoolRoles = user?.reference?.map((item) => item?.role).filter(Boolean) ?? [];
  const mergedRoles = [...classRoles, ...schoolRoles, ...(user?.roles ?? [])];

  return formatSchoolRoleLabels(mergedRoles, {
    isPaymentAdmin: Boolean(user?.is_payment_admin),
    isAdmin: Boolean(user?.reference?.some((item) => item?.is_admin) || user?.is_admin),
  });
};
